import { cartToDataLayerInfo, convertProductToGA4, pageToDataLayerInfo } from '../utils';
import { addToDataLayer, type DataLayerEvent } from '..';

export enum ListType {
  ProductListingPage = 'product-listing-page',
  ProductListBlock = 'product-list-block',
}

export type ItemEventType = {
  product: Excite.Commerce.IProductPageApiModel | NordicNest.Api.VariantLinkApiModel;
  list_name?: string;
  list_id?: string;
  list_type?: ListType;
  list_pagination_index?: number;
  list_pagination_max?: number;
  index?: number;
};

export type ItemListEventType = {
  products: Array<NordicNest.Api.PartialProductApiModel>;
  list_name?: string;
  list_id?: string;
  list_type?: ListType;
  list_pagination_index?: number;
  list_pagination_max?: number;
  defaultPaginationSize?: number;
};

export function addViewItemEventToDataLayer(data: ItemListEventType) {
  const pageData = pageToDataLayerInfo();

  const maxItemsPerEvent = 20;
  let itemStartIndex = 0;

  for (let i = 0; i < data.products.length; i += maxItemsPerEvent) {
    itemStartIndex += i;

    const ga4Event: DataLayerEvent = {
      event: 'view_item_list',
      ecommerce: {
        item_list_id: data.list_id,
        item_list_name: data.list_name,
        page_name: pageData.page_name,
        page_type: pageData.page_type,
        page_brand: pageData.page_brand,
        page_path: pageData.page_path,
        page_location: pageData.page_location,
        item_list_type: data.list_type,
        item_list_page_index: data.list_pagination_index,
        item_list_max_page_index: data.list_pagination_max,
        items: data.products.slice(i, i + maxItemsPerEvent).map((item, j) => ({
          ...convertProductToGA4(item.trackingProductInformation, {}, calculateListItemIndex(data, itemStartIndex + j)),
          item_list_id: data.list_id,
          item_list_name: data.list_name,
          item_list_index: calculateListItemIndex(data, itemStartIndex + j),
          quantity: 1,
        })),
      },
    };
    addToDataLayer(ga4Event);
  }
}

export function addSelectItemEventToDataLayer(data: ItemEventType) {
  const pageData = pageToDataLayerInfo();
  if (data.product?.trackingProductInformation) {
    const ga4Event: DataLayerEvent = {
      event: 'select_item',
      ecommerce: {
        item_list_id: data.list_id,
        item_list_name: data.list_name,
        page_name: pageData.page_name,
        page_type: pageData.page_type,
        page_brand: pageData.page_brand,
        page_path: pageData.page_path,
        page_location: pageData.page_location,
        item_list_type: data.list_type,
        item_list_page_index: data.list_pagination_index,
        item_list_max_page_index: data.list_pagination_max,
        items: [
          {
            ...convertProductToGA4(data.product.trackingProductInformation ?? {}),
            item_list_id: data.list_id,
            item_list_name: data.list_name,
            item_list_index: data.index,
            quantity: 1,
          },
        ],
      },
    };
    addToDataLayer(ga4Event);
  }
}

function calculateListItemIndex(data: ItemListEventType, index: number): number {
  if (!data.list_pagination_index || !data.defaultPaginationSize) return index + 1;

  return (data.list_pagination_index - 1) * data.defaultPaginationSize + index + 1;
}

export async function addItemViewEvent(product: Excite.Commerce.TrackingProductInformation) {
  if (product) {
    const ga4Event: DataLayerEvent = {
      event: 'view_item',
      ecommerce: {
        ...(await cartToDataLayerInfo()),
        items: [
          {
            ...convertProductToGA4(product),
          },
        ],
      },
    };
    addToDataLayer(ga4Event);
  }
}
